import { isTableAnswered, type FormQuestion } from "@kushya/shared";

import type {
  MultiChoiceValue,
  SingleChoiceValue,
} from "@/components/form-questions/types";
import { isSignatureAnswerComplete } from "@/utils/signature";

type MultiChoiceQuestion = Extract<FormQuestion, { type: "multiple_choice_multi" }>;

const normaliseString = (value: unknown) =>
  typeof value === "string" ? value.trim() : "";

const singleChoiceAnswered = (value: unknown): boolean => {
  const resolved = value as SingleChoiceValue | undefined;
  if (!resolved) {
    return false;
  }
  if (resolved.optionId) {
    return true;
  }
  return Boolean(normaliseString(resolved.otherText));
};

const multiChoiceAnswerCount = (value: unknown): number => {
  const resolved = value as MultiChoiceValue | undefined;
  if (!resolved) {
    return 0;
  }
  const optionCount = Array.isArray(resolved.optionIds)
    ? resolved.optionIds.filter(Boolean).length
    : 0;
  const otherCount = normaliseString(resolved.otherText) ? 1 : 0;
  return optionCount + otherCount;
};

const getMultiChoiceSelectionValid = (
  question: MultiChoiceQuestion,
  value: unknown,
): boolean => {
  const resolved = value as MultiChoiceValue | undefined;
  const selections = multiChoiceAnswerCount(resolved);
  if (selections === 0) {
    return false;
  }
  if (question.minSelections && selections < question.minSelections) {
    return false;
  }
  return true;
};

export const isAnswerProvided = (question: FormQuestion, answer: unknown): boolean => {
  switch (question.type) {
    case "multiple_choice_single": {
      return singleChoiceAnswered(answer);
    }
    case "multiple_choice_multi": {
      const multiQuestion = question as MultiChoiceQuestion;
      if (!getMultiChoiceSelectionValid(multiQuestion, answer)) {
        return false;
      }
      if (multiQuestion.maxSelections) {
        const resolved = answer as MultiChoiceValue | undefined;
        const optionCount = resolved?.optionIds?.filter(Boolean).length ?? 0;
        const otherProvided = Boolean(normaliseString(resolved?.otherText));
        return (
          optionCount + (otherProvided ? 1 : 0) <= multiQuestion.maxSelections
        );
      }
      return true;
    }
    case "short_text":
    case "long_text":
    case "email":
    case "phone_number":
    case "time":
    case "date": {
      return Boolean(normaliseString(answer));
    }
    case "read_only_text": {
      return true;
    }
    case "link": {
      return true;
    }
    case "rich_text": {
      return true;
    }
    case "image": {
      return true;
    }
    case "divider": {
      return true;
    }
    case "callout": {
      return true;
    }
    case "signature": {
      return isSignatureAnswerComplete(
        question as Extract<FormQuestion, { type: "signature" }>,
        answer,
      );
    }
    case "table": {
      return isTableAnswered(question, answer);
    }
    case "file_upload": {
      if (!answer) return false;
      if (Array.isArray(answer)) {
        return answer.length > 0;
      }
      // Legacy single-object format
      return typeof answer === 'object' && 'url' in answer;
    }
    default: {
      return answer !== undefined && answer !== null && answer !== "";
    }
  }
};

export const shouldRequireAnswer = (question: FormQuestion) =>
  question.type === "read_only_text" ||
  question.type === "link" ||
  question.type === "rich_text" ||
  question.type === "image" ||
  question.type === "divider" ||
  question.type === "callout"
    ? false
    : Boolean(question.required || question.validation?.required);
